import {io, type Socket} from 'socket.io-client'
import type {PresenceRoomState} from '~/src/models/presence'
import {presenceDebug, presenceDebugWarn} from './presenceDebug'

let presenceSocket: Socket | null = null
let connectionCount = 0
let getPresenceAuthToken: () => string | undefined = () => undefined

/** Register Redux (or test) token getter — called once at app startup. */
export function configurePresenceSocketAuth(getter: () => string | undefined): void {
  getPresenceAuthToken = getter
}
/** Ref-counted room subscriptions per job order (shared across all cards). */
const subscribedRooms = new Map<string, number>()
/** Job order the current socket has joined via presence:join (detail page). */
let activeJoinJobOrderId: string | null = null

function getBackendOrigin(): string {
  return (import.meta.env.VITE_BACKEND_URL ?? '').replace(/\/$/, '')
}

export function normalizeJobOrderId(id: string | undefined): string | undefined {
  if (!id) return undefined
  try {
    return decodeURIComponent(String(id).trim())
  } catch {
    return String(id).trim()
  }
}

export function isJobOrderSummaryRoute(pathname: string, jobOrderId: string): boolean {
  const normalizedId = normalizeJobOrderId(jobOrderId)
  if (!normalizedId) return false
  return pathname.includes(`/manage-job-orders/summary/${normalizedId}`)
}

/** Leave join only when navigation moved off this job order's summary page. */
export function leaveActiveJoinIfNavigatedAway(jobOrderId: string): void {
  if (!activeJoinJobOrderId) return
  const normalizedId = normalizeJobOrderId(jobOrderId)
  if (!normalizedId || activeJoinJobOrderId !== normalizedId) return
  if (isJobOrderSummaryRoute(window.location.pathname, normalizedId)) return
  leaveActiveJoinSession()
}

function emitSubscribe(jobOrderId: string): void {
  const socket = getPresenceSocket()
  presenceDebug('emit presence:subscribe', {
    jobOrderId,
    socketId: socket.id,
    connected: socket.connected,
  })
  socket.emit('presence:subscribe', {jobOrderId})
}

function resubscribeAllRooms(): void {
  if (!presenceSocket?.connected) return
  const roomIds = [...subscribedRooms.keys()]
  presenceDebug('socket connect — resubscribe all rooms', {
    roomIds,
    count: roomIds.length,
    socketId: presenceSocket.id,
  })
  for (const jobOrderId of roomIds) {
    emitSubscribe(jobOrderId)
  }
}

function attachSocketDiagnostics(socket: Socket): void {
  socket.on('connect', () => {
    presenceDebug('socket connected', {
      socketId: socket.id,
      origin: getBackendOrigin(),
      namespace: '/presence',
    })
  })
  socket.on('disconnect', (reason) => {
    presenceDebugWarn('socket disconnected', {reason, socketId: socket.id})
  })
  socket.on('connect_error', (err) => {
    presenceDebugWarn('socket connect_error', {
      message: err.message,
      origin: getBackendOrigin(),
    })
  })
}

function ensureConnectHandler(): void {
  const socket = getPresenceSocket()
  socket.off('connect', resubscribeAllRooms)
  socket.on('connect', resubscribeAllRooms)
}

export function getPresenceSocket(): Socket {
  if (!presenceSocket) {
    const origin = getBackendOrigin()
    presenceDebug('creating presence socket', {origin, namespace: '/presence'})
    presenceSocket = io(`${origin}/presence`, {
      transports: ['polling', 'websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      auth: (cb) => {
        const token = getPresenceAuthToken()
        cb(token ? {token} : {})
      },
    })
    attachSocketDiagnostics(presenceSocket)
    ensureConnectHandler()

    presenceSocket.on('presence:update', (state: PresenceRoomState) => {
      presenceDebug('recv presence:update', {
        jobOrderId: state.jobOrderId,
        userCount: state.users?.length ?? 0,
        users: state.users?.map((u) => ({
          userId: u.userId,
          displayName: u.displayName,
          isIdle: u.isIdle,
        })),
      })
    })
  }
  return presenceSocket
}

export function retainPresenceSocket(): void {
  connectionCount += 1
  const socket = getPresenceSocket()
  presenceDebug('retain socket', {connectionCount, connected: socket.connected})
  if (!socket.connected) {
    socket.connect()
  }
}

export function releasePresenceSocket(): void {
  connectionCount = Math.max(0, connectionCount - 1)
  presenceDebug('release socket', {connectionCount})
  if (connectionCount === 0 && presenceSocket) {
    presenceDebug('disconnecting presence socket (no refs)')
    leaveActiveJoinSession()
    presenceSocket.off('connect', resubscribeAllRooms)
    presenceSocket.disconnect()
    presenceSocket = null
  }
}

/**
 * Subscribe to presence updates for a job order (listen-only, not registered in room).
 * Ref-counted so multiple cards for the same job order share one subscription.
 */
export function subscribePresenceRoom(jobOrderId: string): () => void {
  const normalizedId = normalizeJobOrderId(jobOrderId)
  if (!normalizedId) {
    presenceDebugWarn('subscribe skipped — empty jobOrderId', {raw: jobOrderId})
    return () => {}
  }

  retainPresenceSocket()
  const socket = getPresenceSocket()

  const count = subscribedRooms.get(normalizedId) ?? 0
  subscribedRooms.set(normalizedId, count + 1)

  presenceDebug('subscribePresenceRoom', {
    jobOrderId: normalizedId,
    refCount: count + 1,
    isNewRoom: count === 0,
    connected: socket.connected,
    allRooms: Object.fromEntries(subscribedRooms),
  })

  // Always request a snapshot so remounted cards (e.g. manage-orders CardCombo) get current viewers.
  if (socket.connected) {
    emitSubscribe(normalizedId)
  } else {
    presenceDebug('subscribe deferred until socket connects', {jobOrderId: normalizedId})
  }

  return () => {
    const next = (subscribedRooms.get(normalizedId) ?? 1) - 1
    presenceDebug('unsubscribePresenceRoom', {jobOrderId: normalizedId, refCountAfter: next})
    if (next <= 0) {
      subscribedRooms.delete(normalizedId)
      if (socket.connected) {
        presenceDebug('emit presence:leave', {jobOrderId: normalizedId})
        socket.emit('presence:leave', {jobOrderId: normalizedId})
      }
    } else {
      subscribedRooms.set(normalizedId, next)
    }
    releasePresenceSocket()
  }
}

export function emitPresenceJoin(payload: {
  jobOrderId: string
  userId: string
  displayName: string
  userEmail: string
}): void {
  const normalizedId = normalizeJobOrderId(payload.jobOrderId)
  if (!normalizedId) return

  activeJoinJobOrderId = normalizedId
  const socket = getPresenceSocket()
  const joinPayload = {...payload, jobOrderId: normalizedId}

  const sendJoin = () => {
    presenceDebug('emit presence:join', {
      ...joinPayload,
      socketId: socket.id,
      connected: socket.connected,
    })
    socket.emit('presence:join', joinPayload)
  }

  if (socket.connected) {
    sendJoin()
  } else {
    socket.once('connect', sendJoin)
  }
}

export function emitPresenceLeave(jobOrderId: string): void {
  const normalizedId = normalizeJobOrderId(jobOrderId)
  if (!normalizedId) return

  if (activeJoinJobOrderId === normalizedId) {
    activeJoinJobOrderId = null
  }

  if (!presenceSocket) return

  const sendLeave = () => {
    presenceDebug('emit presence:leave (join session)', {
      jobOrderId: normalizedId,
      socketId: presenceSocket?.id,
    })
    presenceSocket?.emit('presence:leave', {jobOrderId: normalizedId})
  }

  if (presenceSocket.connected) {
    sendLeave()
  } else {
    presenceSocket.once('connect', sendLeave)
  }
}

/** Leave the detail-page join session if still active (e.g. navigating away). */
export function leaveActiveJoinSession(): void {
  if (!activeJoinJobOrderId) return
  const jobOrderId = activeJoinJobOrderId
  activeJoinJobOrderId = null
  emitPresenceLeave(jobOrderId)
}

export function onPresenceUpdate(handler: (state: PresenceRoomState) => void): () => void {
  const socket = getPresenceSocket()
  const listener = (state: PresenceRoomState) => handler(state)
  socket.on('presence:update', listener)
  return () => {
    socket.off('presence:update', listener)
  }
}

/** Expose room ref-counts in devtools: `window.__presenceDebugState()` */
export function getPresenceDebugState(): {
  connectionCount: number
  subscribedRooms: Record<string, number>
  activeJoinJobOrderId: string | null
  socketConnected: boolean
  socketId: string | undefined
} {
  return {
    connectionCount,
    subscribedRooms: Object.fromEntries(subscribedRooms),
    activeJoinJobOrderId,
    socketConnected: presenceSocket?.connected ?? false,
    socketId: presenceSocket?.id,
  }
}

/** Reset singleton for unit tests. */
export function resetPresenceSocketForTesting(): void {
  if (presenceSocket) {
    presenceSocket.off('connect', resubscribeAllRooms)
    presenceSocket.disconnect()
    presenceSocket = null
  }
  connectionCount = 0
  subscribedRooms.clear()
  activeJoinJobOrderId = null
}

if (import.meta.env.DEV || import.meta.env.VITE_MSP_DEBUG === 'true') {
  ;(window as Window & {__presenceDebugState?: typeof getPresenceDebugState}).__presenceDebugState =
    getPresenceDebugState
}
